import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Store } from 'lucide-react';
import { AuthLayout } from '../components/auth/auth-layout.js';
import { CredentialForm } from '../components/auth/credential-form.js';
import { FormError } from '../components/auth/auth-fields.js';
import { GoogleSignInButton } from '../components/auth/google-sign-in-button.js';
import { Button } from '@tailoring-crm/ui';
import { useApiClient } from '../lib/api/api-context.js';
import { loginWithGoogle, selectShop } from '../lib/auth/auth-api.js';
import type { Authenticated, SelectShopChallenge } from '../lib/auth/auth-contract.js';
import { useAuth } from '../lib/auth/auth-context.js';
import { authErrorMessage } from '../lib/auth/auth-errors.js';
import { readRememberedDevice } from '../lib/auth/remembered-device.js';

/**
 * Signing in, and — only when it is real — choosing between shops.
 *
 * The picker is a STEP of this screen rather than a route of its own, because the selection
 * token that authorises it lives for five minutes and only in memory: putting it in the URL
 * would leak it into browser history, and putting it in a route would lose the session on a
 * refresh and strand somebody halfway signed in.
 *
 * Somebody who belongs to one shop never sees it. That is not an optimisation — it is most
 * users, and a "choose your shop" screen with exactly one row is a question that answers
 * itself and still costs a tap.
 */
export function LoginPage(): ReactElement {
  const { t } = useTranslation();
  const client = useApiClient();
  const auth = useAuth();

  const remembered = readRememberedDevice();
  const [challenge, setChallenge] = useState<SelectShopChallenge | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const googleClientId = (import.meta.env['VITE_GOOGLE_CLIENT_ID'] as string | undefined)?.trim();

  /**
   * Adopting the session is all this screen does. Where it lands — the address they were
   * trying to reach, or the dashboard — is `GuestOnly`'s decision, so that one redirect cannot
   * race another.
   */
  const enter = (session: Authenticated, memberships = challenge?.shops): void => {
    auth.signIn({
      user: session.user,
      shop: session.shop,
      role: session.role,
      sessionId: session.sessionId,
      memberships:
        memberships ??
        (session.shop
          ? [{ tenantId: session.shop.id, name: session.shop.name, role: session.role }]
          : []),
    });
  };

  const chooseShop = (tenantId: string): void => {
    if (!challenge) return;
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        enter(await selectShop(client, { selectionToken: challenge.selectionToken, tenantId }));
      } catch (caught) {
        setError(authErrorMessage(caught, t));
        setBusy(false);
      }
    })();
  };

  if (challenge) {
    return (
      <AuthLayout title={t('auth.chooseShopTitle')} description={t('auth.chooseShopDescription')}>
        <div className="flex flex-col gap-3">
          <FormError message={error} />
          {challenge.shops.map((shop) => (
            <button
              key={shop.tenantId}
              type="button"
              disabled={busy}
              onClick={() => chooseShop(shop.tenantId)}
              className="flex items-center gap-3 rounded-lg border border-border p-3 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            >
              <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Store aria-hidden="true" className="size-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-medium">{shop.name}</span>
                <span className="block text-sm text-muted-foreground">
                  {t(`role.${shop.role}`)}
                </span>
              </span>
            </button>
          ))}

          <Button
            variant="ghost"
            block
            onClick={() => {
              setChallenge(null);
              setError(null);
            }}
          >
            {t('common.cancel')}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.loginTitle')}
      description={t('auth.loginDescription')}
      footer={
        <span>
          {t('auth.noAccount')}{' '}
          <Link
            to="/signup"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            {t('auth.createOne')}
          </Link>
        </span>
      }
    >
      <div className="flex flex-col gap-5">
        {googleClientId ? (
          <>
            <FormError message={error} />
            <GoogleSignInButton
              clientId={googleClientId}
              disabled={busy}
              label={t('auth.signInWithGoogle')}
              onError={() => setError(t('auth.googleUnavailable'))}
              onCredential={(credential) => {
                setBusy(true);
                setError(null);
                void (async () => {
                  try {
                    const result = await loginWithGoogle(client, credential);
                    if (result.status === 'select_shop') {
                      setChallenge(result);
                      return;
                    }
                    enter(result, undefined);
                  } catch (caught) {
                    setError(authErrorMessage(caught, t));
                  } finally {
                    setBusy(false);
                  }
                })();
              }}
            />
            <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              <span>{t('auth.orContinueWithEmail')}</span>
              <span className="h-px flex-1 bg-border" />
            </div>
          </>
        ) : null}

        <CredentialForm
          email={remembered?.email ?? ''}
          initialMethod={remembered?.method ?? 'password'}
          submitLabel={t('auth.signIn')}
          onAuthenticated={(result) => {
            if (result.status === 'select_shop') {
              setChallenge(result);
              return;
            }
            enter(result, undefined);
          }}
        >
          <Link
            to="/forgot-password"
            className="self-center text-sm text-primary underline-offset-4 hover:underline"
          >
            {t('auth.forgotPassword')}
          </Link>
        </CredentialForm>
      </div>
    </AuthLayout>
  );
}

export default LoginPage;
